// src/merch/shapes.js
//
// One place that knows how to draw a sticker's cut shape, so the editor,
// print PDF, colouring sheet, scan importer and previews always agree.
//
// A sticker is { widthMm, heightMm, round?, outline? }:
//   • outline: [[x,y],…] in mm, y DOWN, inside the trim box (from the
//     product model — see tools/merch/extract_zones.py)
//   • round: ellipse filling the trim box (puck)
//   • otherwise: rounded rectangle (cornerRadiusMm)

export function cornerRadiusMm(sticker) {
  return Math.min(sticker.widthMm, sticker.heightMm) * 0.12;
}

// Contour stickers (sticker.contour): the cut line is worked out from the
// artwork (contour.js) and lives in sticker.cutRings: [[x,y]…] rings in mm.
// Before there's any art they show the max-size rounded box.
const ringsOf = (st) => (st.contour && st.cutRings?.length ? st.cutRings : null);

/** Canvas Path2D of the cut shape, fitted to the box (x, y, w, h) in px. */
export function stickerPath2D(sticker, x, y, w, h) {
  const p = new Path2D();
  const rings = ringsOf(sticker);
  if (rings) {
    const sx = w / sticker.widthMm, sy = h / sticker.heightMm;
    for (const r of rings) {
      r.forEach(([px, py], i) => { const X = x + px * sx, Y = y + py * sy; if (i === 0) p.moveTo(X, Y); else p.lineTo(X, Y); });
      p.closePath();
    }
  } else if (sticker.outline?.length) {
    const sx = w / sticker.widthMm, sy = h / sticker.heightMm;
    sticker.outline.forEach(([px, py], i) => {
      const X = x + px * sx, Y = y + py * sy;
      if (i === 0) p.moveTo(X, Y); else p.lineTo(X, Y);
    });
    p.closePath();
  } else if (sticker.round) {
    p.ellipse(x + w / 2, y + h / 2, w / 2, h / 2, 0, 0, Math.PI * 2);
  } else {
    const r = cornerRadiusMm(sticker) * (Math.min(w, h) / Math.min(sticker.widthMm, sticker.heightMm));
    p.roundRect(x, y, w, h, r);
  }
  return p;
}

const f = (v) => (Math.round(v * 100) / 100).toString();

/** PDF path operators for the cut shape. Box in PDF points with (x, yTop)
 *  = top-left corner and PDF y pointing UP; w, h in points. */
export function stickerPdfPath(sticker, x, yTop, w, h) {
  const rings = ringsOf(sticker);
  if (rings) {
    const sx = w / sticker.widthMm, sy = h / sticker.heightMm;
    return rings
      .map((r) => r.map(([px, py], i) => `${f(x + px * sx)} ${f(yTop - py * sy)} ${i ? "l" : "m"}`).concat("h").join("\n"))
      .join("\n");
  }
  if (sticker.outline?.length) {
    const sx = w / sticker.widthMm, sy = h / sticker.heightMm;
    return sticker.outline
      .map(([px, py], i) => `${f(x + px * sx)} ${f(yTop - py * sy)} ${i ? "l" : "m"}`)
      .concat("h")
      .join("\n");
  }
  if (sticker.round) return pdfEllipse(x + w / 2, yTop - h / 2, w / 2, h / 2);
  const r = cornerRadiusMm(sticker) * (Math.min(w, h) / Math.min(sticker.widthMm, sticker.heightMm));
  return pdfRoundRect(x, yTop - h, w, h, r);
}

export function pdfRoundRect(x, y, w, h, r) {
  // y-up; x, y = bottom-left
  r = Math.min(r, w / 2, h / 2);
  const k = 0.5523 * r;
  return [
    `${f(x + r)} ${f(y)} m`,
    `${f(x + w - r)} ${f(y)} l`,
    `${f(x + w - r + k)} ${f(y)} ${f(x + w)} ${f(y + r - k)} ${f(x + w)} ${f(y + r)} c`,
    `${f(x + w)} ${f(y + h - r)} l`,
    `${f(x + w)} ${f(y + h - r + k)} ${f(x + w - r + k)} ${f(y + h)} ${f(x + w - r)} ${f(y + h)} c`,
    `${f(x + r)} ${f(y + h)} l`,
    `${f(x + r - k)} ${f(y + h)} ${f(x)} ${f(y + h - r + k)} ${f(x)} ${f(y + h - r)} c`,
    `${f(x)} ${f(y + r)} l`,
    `${f(x)} ${f(y + r - k)} ${f(x + r - k)} ${f(y)} ${f(x + r)} ${f(y)} c`,
    "h",
  ].join("\n");
}

export function pdfEllipse(cx, cy, rx, ry) {
  const kx = 0.5523 * rx, ky = 0.5523 * ry;
  return [
    `${f(cx + rx)} ${f(cy)} m`,
    `${f(cx + rx)} ${f(cy + ky)} ${f(cx + kx)} ${f(cy + ry)} ${f(cx)} ${f(cy + ry)} c`,
    `${f(cx - kx)} ${f(cy + ry)} ${f(cx - rx)} ${f(cy + ky)} ${f(cx - rx)} ${f(cy)} c`,
    `${f(cx - rx)} ${f(cy - ky)} ${f(cx - kx)} ${f(cy - ry)} ${f(cx)} ${f(cy - ry)} c`,
    `${f(cx + kx)} ${f(cy - ry)} ${f(cx + rx)} ${f(cy - ky)} ${f(cx + rx)} ${f(cy)} c`,
    "h",
  ].join("\n");
}

/** Distance (mm) from a point to the cut outline, for a sticker placed
 *  with its trim box at (ox, oy) mm. Used to erase printed trim lines. */
export function outlineDistance(sticker, ox, oy, x, y) {
  if (sticker.outline?.length) {
    const pts = sticker.outline;
    let best = Infinity;
    for (let i = 0; i < pts.length; i++) {
      const [ax, ay] = pts[i], [bx, by] = pts[(i + 1) % pts.length];
      const Ax = ox + ax, Ay = oy + ay, Bx = ox + bx, By = oy + by;
      const dx = Bx - Ax, dy = By - Ay;
      const L2 = dx * dx + dy * dy || 1e-9;
      const t = Math.max(0, Math.min(1, ((x - Ax) * dx + (y - Ay) * dy) / L2));
      const d = Math.hypot(x - (Ax + t * dx), y - (Ay + t * dy));
      if (d < best) best = d;
    }
    return best;
  }
  const w = sticker.widthMm, h = sticker.heightMm;
  const px = x - (ox + w / 2), py = y - (oy + h / 2);
  if (sticker.round) return Math.abs((Math.hypot(px / (w / 2), py / (h / 2)) - 1) * Math.min(w, h) / 2);
  const r = Math.min(cornerRadiusMm(sticker), w / 2, h / 2);
  const qx = Math.abs(px) - (w / 2 - r), qy = Math.abs(py) - (h / 2 - r);
  return Math.abs(Math.hypot(Math.max(qx, 0), Math.max(qy, 0)) + Math.min(Math.max(qx, qy), 0) - r);
}
